import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserProfile } from '@/auth/user-info';
import { Role } from '@/auth/constants';

// Store the last unauthorized path to prevent redirect loops
let lastUnauthorizedPath: string | null = null;
// Store when the last 401 error occurred
let lastUnauthorizedTime: number = 0;

/**
 * A utility component that detects and prevents redirect loops after 401 errors.
 * This component should be included near the root of the application.
 */
export function NavigationFixUtility() {
  const location = useLocation();
  const navigate = useNavigate();
  const userProfile = useUserProfile();

  useEffect(() => {
    // Skip non-401 paths
    if (!location.pathname.includes('/401')) {
      // Reset the 401 tracking when successfully navigated to a non-401 page
      lastUnauthorizedPath = null;
      return;
    }
    
    // If we don't have the user profile yet, wait
    if (!userProfile || !userProfile.roles) {
      return;
    }

    const roles = userProfile.roles || [];
    const now = Date.now();
    
    // If we're in a potential loop (multiple 401s in quick succession)
    if (lastUnauthorizedPath && now - lastUnauthorizedTime < 2000) {
      // Navigate to a safe route based on user roles
      let safeRoute = '/';
      
      if (roles.includes(Role.Admin)) {
        safeRoute = '/dashboard/overview';
      } else if (roles.includes(Role.Finance)) {
        safeRoute = '/finances/invoices';
      } else if (roles.includes(Role.Teacher) || roles.includes(Role.Supervisor)) {
        safeRoute = '/children/groups';
      } else if (roles.includes(Role.Contractor)) {
        safeRoute = '/tasks';
      } else {
        safeRoute = '/reports/daily';
      }
      
      console.warn('Detected 401 navigation loop, redirecting to safe route:', safeRoute);
      // Use replace to avoid building up history
      navigate(safeRoute, { replace: true });
      // Reset tracking
      lastUnauthorizedPath = null;
      return;
    }
    
    // Track this 401 occurrence
    lastUnauthorizedPath = location.pathname;
    lastUnauthorizedTime = now;
  }, [location.pathname, navigate, userProfile]);

  // This is a utility component that doesn't render anything
  return null;
} 